import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

// The veteran timeline (client-intake overhaul): honest status, 30s poll.
export const useOnboardingStatus = (opts = {}) => {
  return useQuery(
    ['onboarding-status'],
    async () => {
      const res = await axios.get('/onboarding/status');
      return res.data;
    },
    // 10s: cheap (a key lookup and an indexed session read; every signed
    // client with the dashboard open polls it since 2026-10-21), and fast enough that
    // "your agreement was just sent" flips the page mid-call instead of
    // making the veteran wait on a slow poll.
    { refetchOnWindowFocus: true, refetchInterval: 10000, staleTime: 5000, ...opts },
  );
};

export const useSubmitSelfReport = (callback, opts = {}) => {
  return useMutation(
    async (facts) => {
      const res = await axios.post('/onboarding/self-report', facts);
      return res.data;
    },
    { ...opts, onSuccess: callback || (() => {}) },
  );
};

export const fetchSaSignLink = async () => {
  const res = await axios.get('/onboarding/sa-sign-link');
  return res.data;
};

export const useGetDocusealSubmission = (opts = {}) => {
  return useQuery(
    ['docuseal-submission'],
    async () => {
      const res = await axios.get(`/onboarding/docuseal/submission`);
      return res.data;
    },
    { refetchOnWindowFocus: false, staleTime: 5000, ...opts },
  );
};

export const useCreateDocusealSubmission = (callback, opts = {}) => {
  return useMutation(
    async () => {
      const res = await axios.post(`/onboarding/docuseal/submission`, {});
      return res.data;
    },
    { ...opts, onSuccess: callback || (() => {}) },
  );
};

export const useMarkDocusealCompleted = (callback, opts = {}) => {
  return useMutation(
    async ({ submissionId }) => {
      const res = await axios.post(`/onboarding/docuseal/submission/completed`, { submissionId });
      return res.data;
    },
    { ...opts, onSuccess: callback || (() => {}) },
  );
};



// The signed client's onboarding checklist (2026-09-28): the dashboard's
// "what we still need" list and who is helping them. Reloads every 30
// seconds while open (2026-10-23), so their specialist's ticks show; React
// Query pauses it in a background tab and stops it when the page closes.
export const useMyOnboarding = (opts = {}) =>
  useQuery(
    ['my-onboarding'],
    async () => (await axios.get('/onboarding/my-onboarding')).data,
    { refetchOnWindowFocus: true, staleTime: 15000, refetchInterval: 30000, ...opts },
  );

// Closes the "You're assigned a new Claims Manager" banner for good
// (Nic 2026-10-26).
export const useDismissCmIntro = (opts = {}) =>
  useMutation(async () => (await axios.post('/onboarding/my-onboarding/cm-intro/dismiss')).data, opts);

// "I've set up VA.gov": tells their specialist (who then checks it).
export const useSayVaGovDone = (opts = {}) =>
  useMutation(
    async () => (await axios.post('/onboarding/my-onboarding/va-gov-done')).data,
    opts,
  );

// "I've done it" for removing their old POA (Verify POA, 2026-10-27): tells
// their specialist, who confirms it. Returns { doneAt }.
export const useSayPoaDone = (opts = {}) =>
  useMutation(
    async () => (await axios.post('/onboarding/my-onboarding/poa-done')).data,
    opts,
  );
